import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { usePageMeta } from '../../../hooks/usePageMeta';
import { fetchTimetableEvents } from '../../../services/eventService';
import { event } from '../../../types/cms-types';
import Header from '../../../components/2025/Header';
import HeroTitle from '../../../components/2025/HeroTitle';
import Footer from '../../../components/2025/Footer';
import FloatingNav from '../../../components/2025/FloatingNav';
import EventCard from '../../../components/2025/EventCard';
import TimeTableGrid from './components/TimeTableGrid';
import { optimizeImage } from '../../../utils/imageOptimizer';
import styles from './styles.module.scss';

const TimeTable2025: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'11/2' | '11/3'>('11/3');
  const [events, setEvents] = useState<event[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState<event | null>(null);
  const [isClosing, setIsClosing] = useState(false);
  const scrollPositionRef = useRef<number | null>(null);

  usePageMeta({
    title: 'タイムテーブル',
    description:
      'かまた祭2025のイベントスケジュール。体育館ステージ、教室企画、部活動発表など、2日間のタイムテーブルを掲載しています。',
  });

  // イベントデータを取得
  useEffect(() => {
    const loadEvents = async () => {
      try {
        setIsLoading(true);
        const fetchedEvents = await fetchTimetableEvents();
        setEvents(fetchedEvents);
      } catch (error) {
        console.error('タイムテーブルデータの取得に失敗しました:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadEvents();
  }, []);

  // 選択されたタブのイベントをフィルタリング
  const filteredEvents = events.filter((event) => event.event_day.includes(activeTab));

  // イベント詳細を表示
  const handleEventClick = (event: event) => {
    setSelectedEvent(event);
  };

  // モーダルを閉じる
  const handleCloseModal = () => {
    setIsClosing(true);

    // アニメーション時間(300ms)待ってから実際にモーダルを閉じる
    setTimeout(() => {
      const savedScrollY = scrollPositionRef.current;

      setSelectedEvent(null);
      setIsClosing(false);

      requestAnimationFrame(() => {
        const body = document.body;
        const html = document.documentElement;
        body.style.position = '';
        body.style.top = '';
        body.style.left = '';
        body.style.right = '';
        body.style.width = '';
        body.style.overflow = '';
        html.style.overflow = '';

        if (savedScrollY !== null) {
          requestAnimationFrame(() => {
            window.scrollTo(0, savedScrollY);
            scrollPositionRef.current = null;
          });
        }
      });
    }, 300);
  };

  // モーダルの開閉に応じてスクロールを制御
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const body = document.body;
    const html = document.documentElement;

    if (selectedEvent) {
      if (scrollPositionRef.current === null) {
        const scrollY = window.scrollY || html.scrollTop || 0;
        scrollPositionRef.current = scrollY;

        body.style.position = 'fixed';
        body.style.top = `-${scrollY}px`;
        body.style.left = '0';
        body.style.right = '0';
        body.style.width = '100%';
        body.style.overflow = 'hidden';
        html.style.overflow = 'hidden';
      }
    }
  }, [selectedEvent]);

  // EventCard用のデータ変換
  const convertEventToEventData = (event: event) => ({
    imageUrl: optimizeImage(event.event_image.url, { width: 800, quality: 80 }),
    title: event.event_title,
    time: event.event_time,
    location: event.event_location,
    description: event.event_description,
    labels: [
      ...event.event_category,
      ...event.event_day,
      ...(event.school || []),
      ...(event.faculties || []),
      ...(event.department || []),
    ],
  });

  return (
    <>
      <div className={styles.timetable}>
        <Header />
        <HeroTitle title="TIMETABLE" subtitle="タイムテーブル" />

        <div className={styles.container}>
          {/* 背景装飾SVG */}
          <svg
            className={styles.decorationSvg}
            width="65"
            height="83"
            viewBox="0 0 65 83"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="15.5556" cy="25.2778" r="15.5556" fill="#F6B8DF" />
            <circle cx="41.5279" cy="49.3056" r="32.2222" stroke="#F6B8DF" strokeWidth="2.5" />
            <circle cx="41.5278" cy="6.52784" r="6.52778" fill="white" />
          </svg>

          <svg
            className={styles.decorationSvg2}
            width="81"
            height="86"
            viewBox="0 0 81 86"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              x="-58"
              y="72.6886"
              width="152.577"
              height="18.2606"
              rx="9.1303"
              transform="rotate(-29.7401 -58 72.6886)"
              fill="url(#paint0_linear_2467_1083)"
            />
            <defs>
              <linearGradient
                id="paint0_linear_2467_1083"
                x1="-58"
                y1="81.8189"
                x2="94.5775"
                y2="81.8189"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#0946F1" />
                <stop offset="1" stopColor="#F1ECE6" />
              </linearGradient>
            </defs>
          </svg>

          <svg
            className={styles.decorationSvg3}
            width="50"
            height="86"
            viewBox="0 0 50 86"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              x="-89"
              y="72.6886"
              width="152.577"
              height="18.2606"
              rx="9.1303"
              transform="rotate(-29.7401 -89 72.6886)"
              fill="url(#paint0_linear_2467_1081)"
            />
            <defs>
              <linearGradient
                id="paint0_linear_2467_1081"
                x1="-89"
                y1="81.8189"
                x2="63.5775"
                y2="81.8189"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#7B8DF2" />
                <stop offset="1" stopColor="#F6B8DF" />
              </linearGradient>
            </defs>
          </svg>

          <svg
            className={styles.decorationSvg4}
            width="120"
            height="141"
            viewBox="0 0 120 141"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="49.6515" cy="70.5972" r="69.7208" fill="#F6B8DF" />
          </svg>

          <svg
            className={styles.decorationSvg5}
            width="86"
            height="181"
            viewBox="0 0 86 181"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              x="-4.08789"
              y="166.771"
              width="299.576"
              height="22.2721"
              rx="11.1361"
              transform="rotate(-34.6137 -4.08789 166.771)"
              fill="url(#paint0_linear_2467_1087)"
              fillOpacity="0.5"
            />
            <defs>
              <linearGradient
                id="paint0_linear_2467_1087"
                x1="-4.08789"
                y1="177.907"
                x2="295.488"
                y2="177.907"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#4D83FE" />
                <stop offset="1" stopColor="#003078" />
              </linearGradient>
            </defs>
          </svg>

          <svg
            className={styles.decorationSvg6}
            width="41"
            height="41"
            viewBox="0 0 41 41"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M28.8162 0.995938C29.858 0.0372442 31.518 0.9718 31.2386 2.35977L28.8438 14.2587C28.7189 14.8795 29.0078 15.51 29.5596 15.8207L40.1361 21.7753C41.3698 22.4699 40.994 24.3374 39.5876 24.5006L27.531 25.8999C26.902 25.9729 26.3916 26.4426 26.2667 27.0633L23.8718 38.9623C23.5925 40.3503 21.7003 40.5699 21.1104 39.2828L16.0538 28.2487C15.7901 27.6731 15.1856 27.3328 14.5567 27.4058L2.50005 28.8052C1.09369 28.9684 0.300086 27.2366 1.34191 26.2779L10.2734 18.0591C10.7393 17.6304 10.8762 16.9504 10.6124 16.3748L5.55584 5.34065C4.96601 4.05356 6.36778 2.76365 7.60149 3.45823L18.178 9.41285C18.7298 9.72347 19.4188 9.6435 19.8847 9.21476L28.8162 0.995938Z"
              fill="#F6B8DF"
            />
          </svg>

          <svg
            className={styles.decorationSvg7}
            width="375"
            height="482"
            viewBox="0 0 375 482"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              x="-149.442"
              y="457.165"
              width="853.012"
              height="37.8276"
              rx="18.9138"
              transform="rotate(-32.9591 -149.442 457.165)"
              fill="url(#paint0_linear_2467_1075)"
              fillOpacity="0.5"
            />
            <defs>
              <linearGradient
                id="paint0_linear_2467_1075"
                x1="-149.442"
                y1="476.078"
                x2="703.569"
                y2="476.078"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#0007D2" />
                <stop offset="1" stopColor="white" />
              </linearGradient>
            </defs>
          </svg>

          {/* 青い境界線でコンテンツを囲む */}
          <div className={styles.timetableWrapper}>
            {/* タブ切り替え - 左上に配置 */}
            <div className={styles.tabs}>
              <button
                className={`${styles.tab} ${activeTab === '11/2' ? styles['tab--active'] : styles['tab--inactive']}`}
                onClick={() => setActiveTab('11/2')}
              >
                11.2<span className={styles.tab__day}>[sun]</span>
              </button>
              <button
                className={`${styles.tab} ${activeTab === '11/3' ? styles['tab--active'] : styles['tab--inactive']}`}
                onClick={() => setActiveTab('11/3')}
              >
                11.3<span className={styles.tab__day}>[mon]</span>
              </button>
            </div>

            {/* タイムテーブルコンテンツ */}
            <div className={styles.timetableContent}>
              {/* 日付表示 */}
              <h2 className={styles.dateTitle}>
                2025/{activeTab}{' '}
                <span className={styles.dayOfWeek}>[{activeTab === '11/2' ? 'sun' : 'mon'}]</span>
              </h2>

              {/* タイムテーブルグリッド */}
              {isLoading ? (
                <div className={styles.loading}>
                  <p>タイムテーブルを読み込み中...</p>
                </div>
              ) : filteredEvents.length > 0 ? (
                <TimeTableGrid
                  events={filteredEvents}
                  onEventClick={handleEventClick}
                  activeTab={activeTab}
                />
              ) : (
                <div className={styles.noData}>
                  <p>この日のタイムテーブルはまだ公開されていません</p>
                </div>
              )}

              {/* 注意書き */}
              <div className={styles.note}>※タイムテーブルは変更になる場合がございます</div>
              <div className={styles.link_flex}>
                <a href="/images/2025/timetable_1.pdf" rel="noreferrer noopener" target="_blank">
                  1日目のタイムテーブルを確認する
                </a>
                <a href="/images/2025/timetable_2.pdf" rel="noreferrer noopener" target="_blank">
                  2日目のタイムテーブルを確認する
                </a>
              </div>
            </div>
          </div>
        </div>

        <Footer />
        <FloatingNav />
      </div>

      {/* イベント詳細モーダル */}
      {selectedEvent &&
        createPortal(
          <div
            className={`${styles.modal} ${isClosing ? styles['modal--closing'] : ''}`}
            onClick={handleCloseModal}
            onTouchMove={(e) => e.preventDefault()}
          >
            <div
              className={`${styles.modal__content} ${styles.eventModal}`}
              onClick={(e) => e.stopPropagation()}
            >
              <EventCard
                events={[convertEventToEventData(selectedEvent)]}
                onClose={handleCloseModal}
              />
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default TimeTable2025;
